"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo } from "react";
import {
  useFieldArray,
  useForm,
  useWatch,
  type Control,
  type UseFormRegister,
} from "react-hook-form";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  NativeSelect,
  Textarea,
} from "@stocks/ui";
import { PURCHASE_ORDER_STATUS } from "@stocks/shared";

import {
  EMPTY_ORDER_FORM,
  EMPTY_ORDER_LINE,
  MaterialsTable,
  aggregateDraftRequired,
  toCreateOrderRequest,
  toOrderFormValues,
  toUpdateOrderRequest,
  useAvailability,
  useBoms,
  useCustomerOptions,
  useProduct,
  useProductOptions,
  usePurchaseOrder,
  withShortage,
  type OrderFormValues,
} from "@/entities/order";
import { CreateCustomerDialog } from "@/features/order/create-customer";
import { useCreateOrder } from "@/features/order/create-order";
import { useUpdateOrder } from "@/features/order/update-order";

const LineRow = ({
  index,
  control,
  register,
  products,
  canRemove,
  onRemove,
  onProductChange,
  error,
}: {
  index: number;
  control: Control<OrderFormValues>;
  register: UseFormRegister<OrderFormValues>;
  products: { id: string; code: string; name: string }[];
  canRemove: boolean;
  onRemove: () => void;
  onProductChange: () => void;
  error?: { optionId?: string; quantity?: string; productId?: string };
}) => {
  const productId = useWatch({ control, name: `lines.${index}.productId` });
  const { data: product, isLoading } = useProduct(productId);
  const options = product?.options.filter((o) => o.isActive) ?? [];

  return (
    <div className='grid items-start gap-2 rounded-md border p-3 md:grid-cols-[1fr_1fr_8rem_auto]'>
      <Field>
        <FieldLabel htmlFor={`line-product-${index}`}>생산 제품</FieldLabel>
        <NativeSelect
          id={`line-product-${index}`}
          className='w-full'
          {...register(`lines.${index}.productId`, {
            required: "제품을 선택하세요.",
            onChange: onProductChange,
          })}
        >
          <option value=''>제품 선택</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.code} · {p.name}
            </option>
          ))}
        </NativeSelect>
        {error?.productId && <FieldError>{error.productId}</FieldError>}
      </Field>
      <Field>
        <FieldLabel htmlFor={`line-option-${index}`}>색상 / 사이즈</FieldLabel>
        <NativeSelect
          id={`line-option-${index}`}
          className='w-full'
          disabled={!productId || isLoading}
          {...register(`lines.${index}.optionId`, {
            required: "옵션을 선택하세요.",
          })}
        >
          <option value=''>
            {productId && !isLoading && options.length === 0
              ? "등록된 옵션 없음"
              : "옵션 선택"}
          </option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.color} / {o.size}
            </option>
          ))}
        </NativeSelect>
        {error?.optionId && <FieldError>{error.optionId}</FieldError>}
      </Field>
      <Field>
        <FieldLabel htmlFor={`line-qty-${index}`}>수량 (벌)</FieldLabel>
        <Input
          id={`line-qty-${index}`}
          type='number'
          min='1'
          step='1'
          {...register(`lines.${index}.quantity`, {
            required: "수량을 입력하세요.",
            validate: (v) =>
              (Number.isInteger(Number(v)) && Number(v) >= 1) ||
              "1 이상의 정수여야 합니다.",
          })}
        />
        {error?.quantity && <FieldError>{error.quantity}</FieldError>}
      </Field>
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='md:mt-6'
        disabled={!canRemove}
        onClick={onRemove}
      >
        삭제
      </Button>
    </div>
  );
};

/** 고객 발주 입력/수정 화면. 수량을 바꾸면 필요 자재·부족 수량이 즉시 다시 계산된다. */
export const OrderForm = ({ orderId }: { orderId?: string }) => {
  const router = useRouter();
  const isEdit = !!orderId;
  const { data: order, isLoading: orderLoading } = usePurchaseOrder(orderId ?? "");

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<OrderFormValues>({ defaultValues: EMPTY_ORDER_FORM });
  const { fields, append, remove } = useFieldArray({ control, name: "lines" });
  const lines = useWatch({ control, name: "lines" });

  const { data: customerPage } = useCustomerOptions();
  const { data: productPage } = useProductOptions();
  const create = useCreateOrder();
  const update = useUpdateOrder(orderId ?? "");

  useEffect(() => {
    if (order) reset(toOrderFormValues(order));
  }, [order, reset]);

  // 입력 중 발주 행 → 필요 자재 → 가용 대조 (서버 계산 규칙과 동일)
  const productIds = useMemo(
    () => [...new Set(lines.map((l) => l.productId).filter(Boolean))],
    [lines],
  );
  const { boms } = useBoms(productIds);
  const required = aggregateDraftRequired(
    lines.map((l) => ({
      productId: l.productId,
      optionId: l.optionId,
      quantity: Number(l.quantity),
    })),
    boms,
  );
  const itemIds = required.map((r) => r.itemId);
  const { availability } = useAvailability(itemIds);
  const materials = withShortage(required, availability);
  const shortCount = materials.filter((m) => m.shortage > 0).length;
  const missingBom = productIds.filter((id) => boms.get(id) === null);
  const productName = (id: string) =>
    productPage?.items.find((p) => p.id === id)?.name ?? id;

  const onSubmit = async (v: OrderFormValues) => {
    if (isEdit) {
      await update.mutateAsync(toUpdateOrderRequest(v));
      router.push(`/orders/${orderId}`);
    } else {
      const res = await create.mutateAsync(toCreateOrderRequest(v));
      router.push(res.data.data ? `/orders/${res.data.data.id}` : "/orders");
    }
  };

  if (isEdit && orderLoading)
    return <p className='text-muted-foreground'>발주를 불러오는 중…</p>;
  if (isEdit && !order)
    return <p className='text-destructive'>발주를 찾을 수 없습니다.</p>;
  if (order && order.status !== PURCHASE_ORDER_STATUS.OPEN)
    return (
      <div className='flex flex-col items-start gap-3'>
        <p className='text-destructive'>취소된 발주는 수정할 수 없습니다.</p>
        <Button asChild variant='outline'>
          <Link href={`/orders/${order.id}`}>발주 상세</Link>
        </Button>
      </div>
    );

  const pending = create.isPending || update.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex max-w-5xl flex-col gap-6'>
      <Card>
        <CardHeader>
          <CardTitle>발주 정보</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-4'>
          <div className='grid gap-4 sm:grid-cols-2'>
            <Field>
              <FieldLabel htmlFor='order-customer'>고객</FieldLabel>
              <div className='flex gap-2'>
                <NativeSelect
                  id='order-customer'
                  className='w-full'
                  disabled={isEdit}
                  {...register("customerId", { required: "고객을 선택하세요." })}
                >
                  <option value=''>고객 선택</option>
                  {customerPage?.items.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} · {c.name}
                    </option>
                  ))}
                  {/* 비활성 고객의 기존 발주 수정 시에도 선택값이 보이도록 */}
                  {order &&
                    !customerPage?.items.some((c) => c.id === order.customerId) && (
                      <option value={order.customerId}>
                        {order.customerCode} · {order.customerName}
                      </option>
                    )}
                </NativeSelect>
                {!isEdit && (
                  <CreateCustomerDialog
                    onCreated={(c) =>
                      setValue("customerId", c.id, { shouldValidate: true })
                    }
                  />
                )}
              </div>
              {errors.customerId && (
                <FieldError>{errors.customerId.message}</FieldError>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor='order-due'>납기일</FieldLabel>
              <Input
                id='order-due'
                type='date'
                {...register("dueDate", { required: "납기일을 입력하세요." })}
              />
              {errors.dueDate && <FieldError>{errors.dueDate.message}</FieldError>}
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor='order-memo'>메모</FieldLabel>
            <Textarea id='order-memo' {...register("memo")} />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>발주 제품</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          {fields.map((f, i) => (
            <LineRow
              key={f.id}
              index={i}
              control={control}
              register={register}
              products={productPage?.items ?? []}
              canRemove={fields.length > 1}
              onRemove={() => remove(i)}
              // 제품을 바꾸면 이전 제품의 옵션 선택은 무효
              onProductChange={() => setValue(`lines.${i}.optionId`, "")}
              error={{
                productId: errors.lines?.[i]?.productId?.message,
                optionId: errors.lines?.[i]?.optionId?.message,
                quantity: errors.lines?.[i]?.quantity?.message,
              }}
            />
          ))}
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => append(EMPTY_ORDER_LINE)}
            >
              제품 행 추가
            </Button>
            <Button asChild variant='link' size='sm'>
              <Link href='/orders/products'>제품 · BOM 관리</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>필요 자재 · 부족 수량 (실시간 계산)</CardTitle>
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          {missingBom.length > 0 && (
            <p className='text-sm text-destructive'>
              BOM 이 등록되지 않은 제품: {missingBom.map(productName).join(", ")} —
              자재 소요량이 계산되지 않습니다.
            </p>
          )}
          <MaterialsTable
            rows={materials.map((m) => ({
              ...m,
              required: m.required,
              available: m.available,
              shortage: m.shortage,
            }))}
            emptyText='제품·옵션·수량을 입력하면 필요 자재가 계산됩니다.'
          />
          {materials.length > 0 && (
            <p
              className={`text-sm ${shortCount > 0 ? "font-medium text-destructive" : "text-muted-foreground"}`}
            >
              {shortCount > 0
                ? `부족한 자재 ${shortCount}종 — 발주는 등록할 수 있으며 입고 후 보충하세요.`
                : "모든 자재의 가용 수량이 충분합니다."}
            </p>
          )}
          <p className='text-xs text-muted-foreground'>
            현재 BOM 과 가용 수량(보유 − 예약, 유효기한 지난 로트 제외) 기준의
            예상값입니다. 저장하면 발주 상세에서 서버가 확정 계산한 결과를 볼 수 있습니다.
          </p>
        </CardContent>
      </Card>

      <div className='flex gap-2'>
        <Button type='submit' disabled={pending}>
          {pending ? "저장 중…" : isEdit ? "발주 수정" : "발주 등록"}
        </Button>
        <Button asChild variant='outline'>
          <Link href={isEdit ? `/orders/${orderId}` : "/orders"}>취소</Link>
        </Button>
      </div>
    </form>
  );
};
